import type { PublicKeyInitData, Signer, TransactionInstruction } from '@solana/web3.js'

import type { ProgramAccount } from '~/store/stake-accounts'
import { useAnchorWallet } from '@solana/wallet-adapter-vue'
import { Authorized, LAMPORTS_PER_SOL, PublicKey, StakeProgram } from '@solana/web3.js'
import { Notify } from 'quasar'
import { withPriorityFees } from '~/feautures/priority-fee'
import { useMonitorTransaction } from '~/hooks/monitor'
import { useConnectionStore } from '~/store/connection'
import { useStakeAccountStore } from '~/store/stake-accounts'
import {
  sendTransaction,
  sendTransactions,
} from '~/utils'

export type CreateStakeOpts = {
  jpoolPercent: boolean
  jstakingPercent: boolean
}

function dec2hex(dec: number) {
  return dec.toString(16).padStart(2, '0')
}

function generateId(len?: number) {
  const arr = new Uint8Array((len || 32) / 2)
  globalThis.crypto.getRandomValues(arr)
  return Array.from(arr, dec2hex).join('')
}

async function findFirstAvailableSeed(
  publicKey: PublicKey,
  stakeAccounts: ProgramAccount[],
): Promise<string> {
  let seed = generateId()
  if (!publicKey) {
    return ''
  }
  while (1) {
    const newStakeAccountPubkey = await PublicKey.createWithSeed(
      publicKey,
      seed,
      StakeProgram.programId,
    )
    const matching = stakeAccounts.find(meta => newStakeAccountPubkey.equals(meta.pubkey))
    if (!matching) {
      break
    }
    seed = generateId()
  }
  return seed
}

export async function createStakePubkeyWithSeed(
  publicKey: PublicKey,
  stakeAccounts: ProgramAccount[],
) {
  const seed = await findFirstAvailableSeed(publicKey, stakeAccounts)
  const stakePubkey = await PublicKey.createWithSeed(publicKey, seed, StakeProgram.programId)
  return {
    seed,
    stakePubkey,
  }
}

export function useDeposit() {
  const connectionStore = useConnectionStore()
  const stakeAccountStore = useStakeAccountStore()

  const { monitorTransaction, sending } = useMonitorTransaction()
  const wallet = useAnchorWallet()

  const loading = ref(false)

  const depositing = computed(() => loading.value || sending.value)

  const createStakeInstructions = async (publicKey: PublicKey, voteKey: string, lamports: number): Promise<TransactionInstruction[]> => {
    if (!lamports) {
      return []
    }
    const stakeData = await createStakePubkeyWithSeed(publicKey, stakeAccountStore.data)
    const newStakeSeed = stakeData.seed
    const stakePubkey = stakeData.stakePubkey

    const createInstructions = StakeProgram.createAccountWithSeed({
      fromPubkey: publicKey,
      stakePubkey,
      basePubkey: publicKey,
      seed: newStakeSeed,
      authorized: new Authorized(publicKey, publicKey),
      lamports,
    })

    const delegateInstructions = StakeProgram.delegate({
      stakePubkey,
      authorizedPubkey: publicKey,
      votePubkey: new PublicKey(voteKey),
    })

    return [
      ...createInstructions.instructions,
      ...delegateInstructions.instructions,
    ]
  }

  return {
    loading,
    depositing,

    /**
     * Delegate an existed stake to a validator and activate (native solana staking)
     */
    delegateAccount: async (stakePubkeys: PublicKey[], voteKey: PublicKeyInitData, reloadStakes = false) => {
      if (!wallet.value?.publicKey) {
        Notify.create({ message: 'Connect a wallet', type: 'negative' })
        return
      }
      try {
        const instructions: TransactionInstruction[] = []

        loading.value = true
        for (const stakePubkey of stakePubkeys) {
          if (wallet.value.publicKey) {
            const instruction = StakeProgram.delegate({
              stakePubkey,
              authorizedPubkey: wallet.value.publicKey,
              votePubkey: new PublicKey(voteKey),
            }).instructions

            instructions.push(...instruction)
          }
        }

        await monitorTransaction(
          sendTransaction(connectionStore.connection, wallet.value, await withPriorityFees({ instructions }), []),
          {
            commitment: 'finalized',
            onSuccess: async () => {
              if (reloadStakes) {
                await stakeAccountStore.load()
              }
            },
            onError: () => {},
          },
        )
      } catch (error: any) {
        if (error.message) {
          Notify.create({ message: error.message, type: 'negative' })
        }
        throw error
      } finally {
        loading.value = false
      }
    },

    /**
     * Create stake account and delegate to a validator
     */
    createStake: async (amount: number, voteKey: string) => {
      if (!wallet.value?.publicKey) {
        Notify.create({ message: 'Connect a wallet', type: 'negative' })
        return
      }
      try {
        loading.value = true

        const instructionSet: TransactionInstruction[][] = [
          await withPriorityFees({ instructions: [
            ...(await createStakeInstructions(wallet.value.publicKey, voteKey, amount * LAMPORTS_PER_SOL)),
          ] }),
        ]

        const signerSet: Signer[][] = [[]]
        const labels: string[] = ['Stake']
        const callbacks: ((signature: string) => void)[] = [async (_signature) => {
          await stakeAccountStore.load()
        }]

        await sendTransactions(
          connectionStore.connection,
          wallet.value,
          instructionSet,
          signerSet,
          {
            stopOnError: true,
            onSuccess: async (txId: string, idx: number) => {
              await monitorTransaction(txId, {
                idx: labels[idx],
                onSuccess: callbacks[idx],
              })
              await stakeAccountStore.load()
            },
          },
        )
      } catch (error: any) {
        if (error.message) {
          Notify.create({ message: error.message, type: 'negative' })
        }
        throw error
      } finally {
        loading.value = false
      }
    },
  }
}
